"use client";

import { motion } from "framer-motion";
import { Bath, BedDouble, Loader2, MapPin, Maximize2 } from "lucide-react";
import FairPriceBadge from "./FairPriceBadge";
import { useLocale } from "next-intl";
import Image from "next/image";
import { useEffect, useState } from "react";

import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/thumbs";

import { properties, regions } from "@/data/main-data";
import { useGetCurrentUser } from "@/hooks/auth-hook";
import { useAddToCart } from "@/hooks/user-hooks";
import { addProductToCart } from "@/redux/slices/cartSlice";
import { useDispatch } from "react-redux";
import DeveloperCard from "./DeveloperCard";
import PropertyFeatures from "./PropertyFeatures";
import PropertyGallery from "./Propertygallery";
import AvailabilityModal from "./ShowTime";

export default function PropertiesInfo({ data }: { data: any }) {
  const locale = useLocale();
  const isArabic = locale === "ar";

  const [showVideo, setShowVideo] = useState(false);
  const [showAvailability, setShowAvailability] = useState(false);
  const [changeTime, setChangeTime] = useState([]);

  const dispatch = useDispatch();
  const { mutate, isPending } = useAddToCart();
  const { data: userData } = useGetCurrentUser();

  const allImages = [...(data?.images || [])].filter(Boolean);
  const region = regions.find((e) => e.value === data?.region);
  const propertyType = properties.find((e) => e.value === data?.propertyType);

  const isSale = data?.is_sale;

  const handleBookNow = () => {
    if (!data?.is_sale) {
      setShowAvailability(true);
      return;
    }

    if (!userData?.user) {
      const product = {
        id: data?.id,
        title_en: data?.title_en,
        title_ar: data?.title_ar,
        description_en: data?.description_en,
        description_ar: data?.description_ar,
        thumbnail: data?.thumbnail,
        is_sale: data?.is_sale,
        changeTime,
        price: data?.is_sale
          ? data?.sale_price
          : data?.rent_type === "yearly"
            ? data?.price_per_year
            : data?.price_per_day,
      };
      dispatch(addProductToCart(product));
      return;
    }

    mutate({ property: data?.id, changeTime });
  };

  useEffect(() => {
    if (showVideo) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [showVideo]);

  return (
    <div className={`w-full ${isArabic ? "rtl" : "ltr"} `}>
      <div className=" mx-auto py-10 space-y-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <PropertyGallery
            images={allImages}
            alt={isArabic ? data?.title_ar : data?.title_en}
          />
          <div className="bg-white rounded-2xl p-6  space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-gray-200 pb-3">
              <h2 className="text-2xl font-bold">
                {isArabic ? data?.title_ar : data?.title_en}
              </h2>
              <div className="flex items-center gap-2 text-gray-700">
                <span className="text-main font-extrabold text-xl sm:text-2xl">
                  {isSale
                    ? data?.sale_price?.toLocaleString()
                    : data?.rent_type === "yearly"
                      ? data?.price_per_year?.toLocaleString()
                      : data?.price_per_day?.toLocaleString()}
                </span>
                <Image src="/sar.png" alt="SAR" width={18} height={18} />
                {!isSale && data?.rent_type === "daily" && (
                  <span>/ {isArabic ? "يوم" : "day"}</span>
                )}
                {!isSale && data?.rent_type === "yearly" && (
                  <span>/ {isArabic ? "سنة" : "year"}</span>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-gray-200 pb-3">
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin /> {isArabic ? region?.label_ar : region?.label_en}
              </div>
              <div className="font-extrabold text-main text-right">
                {isArabic ? propertyType?.label_ar : propertyType?.label_en}{" "}
                {isSale
                  ? `- ${isArabic ? "للبيع" : "For Sale"}`
                  : `- ${isArabic ? "للإيجار" : "For Rent"}`}
              </div>
            </div>

            {/* SPECS: bedrooms / bathrooms / area */}
            <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 pb-4">
              {data?.bedrooms > 0 && (
                <div className="flex items-center gap-2 bg-main/5 text-main rounded-xl px-4 py-2.5 font-semibold">
                  <BedDouble size={18} />
                  {data.bedrooms} {isArabic ? "غرف" : "Beds"}
                </div>
              )}
              {data?.bathrooms > 0 && (
                <div className="flex items-center gap-2 bg-main/5 text-main rounded-xl px-4 py-2.5 font-semibold">
                  <Bath size={18} />
                  {data.bathrooms} {isArabic ? "حمام" : "Baths"}
                </div>
              )}
              {data?.area > 0 && (
                <div className="flex items-center gap-2 bg-main/5 text-main rounded-xl px-4 py-2.5 font-semibold">
                  <Maximize2 size={17} />
                  {data.area} {isArabic ? "م²" : "m²"}
                </div>
              )}
            </div>

            {/* Fair-price guarantee badge */}
            <div id="ai-fair">
              <FairPriceBadge data={data} />
            </div>

            <p className="text-gray-800 leading-7">
              {isArabic ? data?.description_ar : data?.description_en}
            </p>

            <DeveloperCard developer={data?.developer} isArabic={isArabic} />

            <div className="flex flex-wrap items-center gap-4">
              {data?.is_sell ? (
                <button
                  disabled
                  className="px-6 py-3 bg-gray-400 text-white font-semibold rounded-xl cursor-not-allowed"
                >
                  {isArabic ? "تم البيع" : "Sold Out"}
                </button>
              ) : (
                <button
                  onClick={handleBookNow}
                  disabled={isPending}
                  className="px-6 py-3 bg-main text-white cursor-pointer font-semibold rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300"
                >
                  {isPending ? (
                    <Loader2 className="animate-spin" />
                  ) : isArabic ? (
                    "احجز الآن"
                  ) : (
                    "Book Now"
                  )}
                </button>
              )}

              {data?.video && (
                <button
                  onClick={() => setShowVideo(true)}
                  className="px-6 py-3 border-main border text-main rounded-xl font-semibold hover:bg-main hover:text-white transition-colors duration-300"
                >
                  {isArabic ? "عرض الفيديو الثلاثي الأبعاد" : "View 3D Video"}
                </button>
              )}
            </div>
          </div>
        </div>

        {showVideo && (
          <motion.div
            className="fixed inset-0 h-screen bg-black/80 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowVideo(false)}
          >
            <motion.div
              className="w-11/12 md:w-3/4 lg:w-1/2 aspect-video bg-black rounded-xl overflow-hidden"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              onClick={(e) => e.stopPropagation()}
            >
              <iframe
                src={data?.video}
                title="Property 3D Video"
                width="100%"
                height="100%"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </motion.div>
          </motion.div>
        )}

        {showAvailability && (
          <AvailabilityModal
            showAvailability={showAvailability}
            data={data}
            changeTime={changeTime}
            setChangeTime={setChangeTime}
            setShowAvailability={setShowAvailability}
            user={userData?.user}
          />
        )}

        <PropertyFeatures
          features={data?.features || []}
          id={data?.id}
          googleLink={{
            latitude: data?.latitude,
            longitude: data?.longitude,
          }}
        />
      </div>
    </div>
  );
}
